import React, { useState, useEffect } from "react";
import { Device } from "types/device.types";
import { formatDistance } from "date-fns";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  LinkButton,
  StatusBadge,
  WifiIcon,
  AlarmIndicator,
} from "styles/devices/devicesList.styles";
import styled from "styled-components";
import { theme } from "styles/theme";
import { useAuth } from "context/AuthContext";
import { buildDeviceDetailsPath } from "utils/deviceNavigation";

interface DevicesTableViewProps {
  devices: Device[];
  selectedDevices: Device[];
  onSelectionChange: (devices: Device[]) => void;
  onFirmwareUpdateClick: () => void;
  onNipperUpdateClick: () => void;
  isUpdating: boolean;
  onSort: (field: string, order: string) => void;
  sortField?: string;
  sortOrder?: string;
  isLoading?: boolean;
  isAdminView?: boolean;
}

const TableHeader = styled.div`
  display: flex;
  align-items: center;
`;

const BulkActionButton = styled(Button)`
  margin-left: 10px;
  background-color: ${theme.colors.primary};
  border-color: ${theme.colors.primary};
  color: ${theme.colors.onPrimary};
  padding: 0.75rem 1.25rem;
  min-height: 44px;
  gap: 0.5rem;

  .p-button-icon-left {
    margin-right: 0.5rem;
  }

  .p-button-icon-right {
    margin-left: 0.5rem;
  }

  &:enabled:hover {
    background-color: ${theme.colors.primaryDark};
    border-color: ${theme.colors.primaryDark};
    color: ${theme.colors.onPrimary};
  }
`;

const DevicesTableView: React.FC<DevicesTableViewProps> = ({
  devices,
  selectedDevices,
  onSelectionChange,
  onFirmwareUpdateClick,
  onNipperUpdateClick,
  isUpdating,
  onSort,
  sortField = "updatedAt",
  sortOrder = "DESC",
  isLoading = false,
  isAdminView = false,
}) => {
  // Local state for sorting
  const [currentSortField, setCurrentSortField] = useState<string>(sortField);
  const [currentSortOrder, setCurrentSortOrder] = useState<string>(sortOrder);
  const { user } = useAuth();
  const isAdmin = isAdminView || user?.role === "ADMIN";
  // Sync local state with props when they change
  useEffect(() => {
    setCurrentSortField(sortField);
    setCurrentSortOrder(sortOrder);
  }, [sortField, sortOrder]);

  // Helper function to format the updated date
  const formatUpdatedDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (error) {
      return "Unknown";
    }
  };

  // Helper function to check if device is pending or rejected
  const isDeviceRestricted = (status: string) => {
    return status === "pending" || status === "rejected";
  };

  // Template for status badge in table
  const statusBodyTemplate = (device: Device) => {
    return (
      device.accessType === "temporary" ? (
        <StatusBadge status="temporary">temporary access</StatusBadge>
      ) : (
        <StatusBadge status={device.linkStatus}>{device.linkStatus}</StatusBadge>
      )
    );
  };

  // Template for action buttons in table
  const actionBodyTemplate = (device: Device) => {
    const restricted = isDeviceRestricted(device.linkStatus);
    if (restricted) {
      return null;
    }

    return (
      <LinkButton
        to={buildDeviceDetailsPath({
          serialNumber: device.serialNumber,
          linkId: device.linkId,
          isAdminView: isAdmin,
        })}
      >
        View Details
      </LinkButton>
    );
  };

  // Template for date in table
  const dateBodyTemplate = (device: Device) => {
    return <span>{formatUpdatedDate(device.updatedAt)}</span>;
  };

  // Template for connection status in table
  const connectionBodyTemplate = (device: Device) => {
    return (
      <WifiIcon
        connected={!!device.iotConnectionStatus?.connected}
        className="pi pi-wifi"
      />
    );
  };

  // Template for alarm indicator in table
  const alarmBodyTemplate = (device: Device) => {
    if (isDeviceRestricted(device.linkStatus)) {
      return null;
    }

    const shadowData = device.shadowData || {};
    const hasAlarm = shadowData.alarms ? shadowData.alarms > 0 : false;

    // Only show an indicator when there is something to flag
    if (!hasAlarm) {
      return null;
    }

    const alarmSeverity = (() => {
      const alarmValue = shadowData.alarms ?? 0;

      if (alarmValue > 255) {
        return "Error";
      } else if (alarmValue >= 1 && alarmValue <= 255) {
        return "Warning";
      } else {
        return "No alarms";
      }
    })();

    return (
      <AlarmIndicator
        $hasAlarm={hasAlarm}
        alarmSeverity={alarmSeverity}
        className="pi pi-exclamation-triangle"
        title="Device has alarms"
      />
    );
  };

  // Handle sorting change
  const handleSort = (event: DataTableSortEvent) => {
    const field = event.sortField;
    const order = event.sortOrder === 1 ? "ASC" : "DESC";

    setCurrentSortField(field);
    setCurrentSortOrder(order);

    onSort(field, order);
  };

  // Header template for the table with action buttons
  const renderHeader = () => {
    if (selectedDevices.length <= 0) return null;
    return (
      <TableHeader>
        <BulkActionButton
          label="Update Lifeguard"
          icon="pi pi-cloud-download"
          onClick={onFirmwareUpdateClick}
          disabled={isUpdating}
        />
        <BulkActionButton
          label="Update Nipper"
          icon="pi pi-server"
          onClick={onNipperUpdateClick}
          disabled={isUpdating}
        />
      </TableHeader>
    );
  };

  return (
    <DataTable
      value={devices}
      rows={10}
      loading={isLoading}
      header={renderHeader()}
      emptyMessage="No devices found."
      selection={selectedDevices}
      onSelectionChange={(e) => {
        // Filter selected devices to remove restricted ones
        const filteredSelection = e.value.filter(
          (device) => !isDeviceRestricted(device.linkStatus)
        );
        onSelectionChange(filteredSelection);
      }}
      selectionMode="multiple"
      dataKey="id"
      isDataSelectable={(e) =>
        !isDeviceRestricted((e.data as Device).linkStatus)
      }
      sortField={currentSortField}
      sortOrder={currentSortOrder === "ASC" ? 1 : -1}
      onSort={handleSort}
      removableSort={false}
    >
      <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
      <Column
        field="iotConnectionStatus.connected"
        header="Online"
        headerStyle={{ width: "4rem" }}
        sortable={false}
        body={connectionBodyTemplate}
      />
      <Column
        field="shadowData.alarms"
        header="Status"
        body={alarmBodyTemplate}
        style={{ width: "4rem" }}
      />
      <Column field="serialNumber" header="Serial Number" sortable />
      <Column field="firmwareVersion" header="Lifeguard Version" sortable />
      <Column field="nipperVersion" header="Nipper Version" sortable />
      {!isAdmin && (
        <Column
          field="linkStatus"
          header="Status"
          body={statusBodyTemplate}
          sortable
        />
      )}
      <Column
        field="updatedAt"
        header="Last Updated"
        body={dateBodyTemplate}
        sortable
      />
      <Column body={actionBodyTemplate} header="Action" />
    </DataTable>
  );
};

export default DevicesTableView;
